<template>
  <div>
    <v-sheet outlined rounded>
      <div class="text-subtitle-1 ml-3 mt-1">{{ $tc('date_range') }}</div>
      <v-row dense class="mx-3 mt-2">
        <v-col lg="6" md="8" cols="12">
          <div class="d-flex align-center">
            <date-picker
              v-model="criteria.createdAfter"
              :timezone="customerTimezone"
              :label="$t('start_date')"
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
            <date-picker
              v-model="criteria.createdBefore"
              :timezone="customerTimezone"
              :label="$t('end_date')"
              end-of-day
              prepend-inner-icon="mdi-calendar"
              reset-on-timezone-change/>
          </div>
        </v-col>
      </v-row>
      <v-alert v-if="rangeError" type="warning" outlined dense class="mx-3">
        {{ rangeError }}
      </v-alert>
      <div class="d-flex justify-end mr-3 mb-2">
        <!--
          Disabled while a load is in flight as well as on an unusable range.
          Vuetify 2's loading prop does not imply disabled
          (vuetify/lib/components/VBtn/VBtn.js), so without this a second click
          would start a second set of requests over the first.
        -->
        <v-btn
          outlined
          color="primary"
          :disabled="rangeError !== null || isBusy"
          :loading="isBusy"
          @click="load()">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
      </div>
    </v-sheet>

    <v-row dense class="mt-2">
      <v-col v-for="card in summaryCards" :key="card.key" cols="6" md="4" lg="2">
        <v-card outlined class="fill-height">
          <v-card-text class="text-center py-3">
            <div class="text-h5 primary--text">{{ card.value }}</div>
            <div class="text-caption">
              {{ card.label }}
              <v-tooltip v-if="card.hint" bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                </template>
                <span>{{ card.hint }}</span>
              </v-tooltip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-progress-linear v-if="summaryLoading" indeterminate/>

    <v-alert v-if="rangeIsEmpty" type="info" outlined dense class="mt-2">
      {{ $t('no_requests_in_range') }}
    </v-alert>

    <v-row dense class="mt-2">
      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 py-2">
            <v-icon class="mr-2" small>mdi-chart-bar</v-icon>
            {{ $t('requests_per_day') }}
          </v-card-title>
          <v-card-text>
            <c3-chart
              v-if="perDay.length > 0"
              ref="requestsChart"
              type="bar"
              :height="chartHeight"
              :data="requestsChartData"
              :config="requestsChartConfig"/>
            <v-skeleton-loader v-else type="image" :height="chartHeight"/>
            <div class="text-caption text--secondary mt-1">{{ $t('statistics_days_are_utc') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="6">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 py-2">
            <v-icon class="mr-2" small>mdi-chart-line</v-icon>
            {{ $t('response_time_trend') }}
          </v-card-title>
          <v-card-text>
            <c3-chart
              v-if="perDay.length > 0"
              ref="responseTimeChart"
              type="line"
              :height="chartHeight"
              :data="responseTimeChartData"
              :config="responseTimeChartConfig"/>
            <v-skeleton-loader v-else type="image" :height="chartHeight"/>
            <div class="text-caption text--secondary mt-1">{{ responseTimeSample }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="12" lg="4">
        <api-access-log-top-list
          :title="$t('top_users')"
          :entity-header="$tc('user')"
          icon="mdi-account"
          :items="topUserItems"
          :loading="topLoading.users"/>
      </v-col>
      <v-col cols="12" lg="4">
        <api-access-log-top-list
          :title="$t('top_customers')"
          :entity-header="$tc('customer')"
          icon="mdi-office-building"
          :items="topCustomerItems"
          :loading="topLoading.customers"/>
      </v-col>
      <v-col cols="12" lg="4">
        <api-access-log-top-list
          :title="$t('top_endpoints')"
          :entity-header="$t('endpoint')"
          icon="mdi-link-variant"
          :items="topEndpointItems"
          :loading="topLoading.endpoints"/>
      </v-col>
    </v-row>
    <div class="text-caption text--secondary mt-1">{{ $t('internal_endpoints_included') }}</div>
    <div v-if="generatedAt" class="text-caption text--secondary">{{ $t('statistics_as_of', { time: generatedAt }) }}</div>
  </div>
</template>

<script>

import { get, map } from 'lodash'
import Moment from 'moment-timezone'
import C3Chart from '@/components/shared/chart/C3Chart'
import DatePicker from '@/components/shared/DatePicker'
import ApiAccessLogTopList from '@/components/api-access-log/ApiAccessLogTopList'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'

/**
 * Widest range the statistics endpoints will serve, in calendar days, counting
 * the days both bounds fall on.
 *
 * Mirrors ApiAccessLogStatistics::MAX_RANGE_DAYS server side, which is the
 * authority: a wider range is a 400 there whatever this file says. Checked here
 * as well so that the range that cannot be answered is reported next to the
 * pickers instead of as a failed request.
 */
const MAX_RANGE_DAYS = 31

/**
 * How much history the page opens on. Deliberately well inside MAX_RANGE_DAYS:
 * the first thing this page does on load is read every log row in its range, so
 * the default is a week rather than the largest range allowed.
 */
const DEFAULT_RANGE_DAYS = 7

/**
 * API usage statistics over the api_access_log table: volume, records extracted,
 * response times, and the busiest callers, customers and endpoints.
 *
 * Root only. The API answers 404 to any other caller, so the parent view is
 * responsible for not mounting this component for one.
 *
 * Each figure is a link into the request browser, filtered to the entity clicked
 * and to the range shown. See drillDownTo(), which is the only place those links
 * are built.
 *
 * The percentile is read from the p95_response_time_ms keys and labelled as the
 * 95th, which the API states in its own payload as percentile: 95. The two are
 * expected to be changed together; nothing here derives the label from the
 * response, because a chart legend and a card title are translated strings rather
 * than something to assemble at runtime.
 */
export default {
  name: 'ApiAccessLogStatistics',
  components: {
    ApiAccessLogTopList,
    C3Chart,
    DatePicker
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    /**
     * Height of both trend charts, in pixels.
     */
    chartHeight: {
      type: Number,
      default: 240
    }
  },
  data () {
    return {
      // What the pickers hold. Only copied into applied by load(), so the
      // figures on screen and the drill-down links always agree with each other.
      criteria: {
        createdAfter: null,
        createdBefore: null
      },
      applied: null,
      // Incremented by every load(). A response may only be committed while its
      // own token is still the current one, which is what stops a slow answer for
      // an earlier range landing on top of a faster answer for a later one.
      loadSequence: 0,
      summary: null,
      summaryLoading: false,
      top: {
        users: [],
        customers: [],
        endpoints: []
      },
      topLoading: {
        users: false,
        customers: false,
        endpoints: false
      }
    }
  },
  computed: {
    /**
     * Whether anything is still loading.
     *
     * @return Boolean
     */
    isBusy () {
      return this.summaryLoading ||
        this.topLoading.users ||
        this.topLoading.customers ||
        this.topLoading.endpoints
    },

    /**
     * Why the picked range cannot be used, or null when it can.
     *
     * @return String|null
     */
    rangeError () {
      const start = this.criteria.createdAfter
      const end = this.criteria.createdBefore

      if (!start || !end) {
        return this.$t('date_range_required')
      }

      // Read in the customer's timezone, which is the timezone the pickers wrote
      // them in, so that the days counted below are the days that were picked.
      const from = Moment.tz(start, this.customerTimezone)
      const to = Moment.tz(end, this.customerTimezone)

      if (to.isBefore(from)) {
        return this.$t('end_date_before_start_date')
      }

      // Calendar days inclusive of both endpoint days, matching
      // ApiAccessLogStatisticsController::calendarDays(). Counting elapsed
      // seconds instead refused a genuine 31 day range that crossed a daylight
      // saving change, because it is 31 days and an hour long.
      const days = to.clone().startOf('day').diff(from.clone().startOf('day'), 'days') + 1

      if (days > MAX_RANGE_DAYS) {
        return this.$t('date_range_too_wide', { days: MAX_RANGE_DAYS })
      }

      return null
    },

    /**
     * The query parameters every statistics request carries.
     *
     * @return Object
     */
    rangeParams () {
      return {
        created_after: get(this.applied, 'createdAfter'),
        created_before: get(this.applied, 'createdBefore')
      }
    },

    /**
     * The daily series, oldest first, with a point for every day in the range
     * including the ones with no traffic.
     *
     * @return Array
     */
    perDay () {
      return get(this.summary, 'per_day', [])
    },

    /**
     * Whether the range loaded and contained nothing, which is worth saying
     * outright rather than showing six zeroes.
     *
     * @return Boolean
     */
    rangeIsEmpty () {
      return this.summary !== null && get(this.summary, 'totals.requests') === 0
    },

    /**
     * The summary cards.
     *
     * @return Array
     */
    summaryCards () {
      const totals = get(this.summary, 'totals', {})

      return [
        {
          key: 'requests',
          label: this.$t('total_requests'),
          value: this.formatCount(totals.requests)
        },
        {
          key: 'unique_users',
          label: this.$t('unique_users'),
          value: this.formatCount(totals.unique_users),
          hint: this.$t('unique_users_hint')
        },
        {
          key: 'unique_customers',
          label: this.$t('unique_customers'),
          value: this.formatCount(totals.unique_customers)
        },
        {
          key: 'records_returned',
          label: this.$t('records_returned'),
          value: this.formatCount(totals.records_returned)
        },
        {
          key: 'avg_response_time',
          label: this.$t('avg_response_time'),
          value: this.formatMilliseconds(totals.avg_response_time_ms)
        },
        {
          key: 'p95_response_time',
          label: this.$t('p95_response_time'),
          value: this.formatMilliseconds(totals.p95_response_time_ms),
          hint: this.$t('p95_response_time_hint')
        }
      ]
    },

    /**
     * How much of the range the two response time figures speak for. The API
     * leaves response_time_ms null when it cannot tell when a request started,
     * so the sample can be smaller than the request count.
     *
     * @return String
     */
    responseTimeSample () {
      const totals = get(this.summary, 'totals')

      if (!totals) {
        return ''
      }

      return this.$t('response_time_sample', {
        timed: Number(totals.timed_requests).toLocaleString(),
        total: Number(totals.requests).toLocaleString()
      })
    },

    /**
     * When the figures on screen were calculated, in the customer's timezone.
     * Shown because the API serves a cached answer for a few minutes, so "now"
     * would be a lie.
     *
     * @return String|null
     */
    generatedAt () {
      const generated = get(this.summary, 'generated_at')

      return generated
        ? Moment(generated).tz(this.customerTimezone).format('YYYY-MM-DD HH:mm z')
        : null
    },

    /**
     * Requests per day.
     *
     * @return Object
     */
    requestsChartData () {
      return {
        x: 'date',
        columns: [
          ['date', ...map(this.perDay, 'date')],
          ['requests', ...map(this.perDay, 'requests')]
        ],
        names: {
          requests: this.$tc('request', 2)
        },
        colors: {
          requests: '#466cb4'
        }
      }
    },

    /**
     * @return Object
     */
    requestsChartConfig () {
      return {
        legend: {
          show: false
        },
        bar: {
          width: {
            ratio: 0.6
          }
        },
        axis: {
          x: this.dayAxis,
          y: {
            min: 0,
            padding: {
              bottom: 0
            },
            tick: {
              format: value => value.toLocaleString(),
              culling: true
            }
          }
        }
      }
    },

    /**
     * Average and 95th percentile response time per day.
     *
     * @return Object
     */
    responseTimeChartData () {
      return {
        x: 'date',
        columns: [
          ['date', ...map(this.perDay, 'date')],
          ['average', ...map(this.perDay, 'avg_response_time_ms')],
          ['p95', ...map(this.perDay, 'p95_response_time_ms')]
        ],
        names: {
          average: this.$t('avg_response_time'),
          p95: this.$t('p95_response_time')
        },
        colors: {
          average: '#466cb4',
          p95: '#E09037'
        }
      }
    },

    /**
     * @return Object
     */
    responseTimeChartConfig () {
      return {
        point: {
          show: false
        },
        line: {
          // A day with no traffic has no response time at all, and joining
          // across it is more readable than a broken line, so long as the gap
          // itself is visible on the requests chart next to it.
          connectNull: true
        },
        axis: {
          x: this.dayAxis,
          y: {
            min: 0,
            padding: {
              bottom: 0
            },
            tick: {
              format: value => value.toLocaleString(),
              culling: true
            }
          }
        }
      }
    },

    /**
     * The shared x axis of both charts: one tick per day, up to a limit that
     * keeps the labels readable on a month wide range.
     *
     * @return Object
     */
    dayAxis () {
      return {
        type: 'timeseries',
        tick: {
          format: '%Y-%m-%d',
          count: Math.min(this.perDay.length, 8),
          centered: true,
          culling: true
        }
      }
    },

    /**
     * @return Array
     */
    topUserItems () {
      return this.topItems(this.top.users, item => ({
        key: `user-${item.user_id}`,
        // The email is null when the user has since been hard deleted, and the
        // id is still worth showing and still worth drilling into.
        label: item.email || `#${item.user_id}`,
        to: this.drillDownTo({ user_id: item.user_id })
      }))
    },

    /**
     * @return Array
     */
    topCustomerItems () {
      return this.topItems(this.top.customers, item => ({
        key: `customer-${item.customer_id}`,
        label: item.name || `#${item.customer_id}`,
        to: this.drillDownTo({ customer_id: item.customer_id })
      }))
    },

    /**
     * @return Array
     */
    topEndpointItems () {
      return this.topItems(this.top.endpoints, item => ({
        key: `endpoint-${item.endpoint}`,
        label: item.endpoint,
        internal: item.internal,
        to: this.drillDownTo({ endpoint: item.endpoint })
      }))
    }
  },
  methods: {
    /**
     * Turns API rows into rows for a top list, adding the three figures every
     * list shows the same way.
     *
     * Returns nothing at all while there is no applied range, which is what makes
     * a link without both date bounds impossible rather than merely unlikely: a
     * row is only offered once there is a range for its link to carry. See
     * drillDownTo() for why that matters.
     *
     * @param {Array} rows Items as the API returned them.
     * @param {Function} identify Maps one row to its key, label and link.
     * @return Array
     */
    topItems (rows, identify) {
      if (this.applied === null) {
        return []
      }

      return map(rows, row => ({
        ...identify(row),
        requests: row.requests,
        recordsReturned: row.records_returned,
        avgResponseTimeMs: row.avg_response_time_ms
      }))
    },

    /**
     * The router target for a drill-down into the request browser.
     *
     * Both date bounds are always sent, and that is the load bearing part of
     * this method rather than a detail. The browser defaults created_after to
     * the start of today when it is absent, so a link that passed only
     * created_before, or only the entity, would silently narrow the range the
     * figures were calculated over to today and show numbers that do not match
     * the row that was clicked.
     *
     * The bounds come from applied rather than criteria, so a link always
     * describes the range the figures on screen were calculated over, even if
     * someone has since moved a date picker without searching again.
     *
     * Parameter names are the API's own (user_id, customer_id, endpoint,
     * created_after, created_before), which is also what
     * ApiAccessLogIndex.vue reads off the query string.
     *
     * Only called from topItems(), which does not call it without an applied
     * range, so both bounds are always real values here rather than undefined
     * that the query string serializer would drop.
     *
     * @param {Object} filters One entity filter, keyed as the API names it.
     * @return Object
     */
    drillDownTo (filters) {
      return {
        path: '/api-requests',
        query: {
          ...filters,
          created_after: this.applied.createdAfter,
          created_before: this.applied.createdBefore
        }
      }
    },

    /**
     * The range the page opens on: the last DEFAULT_RANGE_DAYS days, in the
     * customer's timezone, whole days at both ends.
     *
     * @return Object
     */
    getDefaultCriteria () {
      const today = Moment().tz(this.customerTimezone)

      return {
        createdAfter: today.clone().subtract(DEFAULT_RANGE_DAYS - 1, 'days').startOf('day').format(),
        createdBefore: today.clone().endOf('day').format()
      }
    },

    /**
     * Applies the picked range and reloads every panel.
     *
     * The four requests are deliberately not chained: they are independent
     * aggregates of very different cost, and the summary cards should not wait
     * for the top endpoints query, which is the slowest of them.
     *
     * Everything already on screen is discarded here, in the same tick that
     * applied moves, rather than as each replacement arrives. Rows outliving the
     * range they were computed over is not a cosmetic problem: drillDownTo()
     * stamps whatever applied now holds onto every row's link, so a row left
     * behind for even a moment is a clickable link to a range those figures were
     * never computed over, which is the one thing the drill-down contract exists
     * to prevent.
     *
     * Every load starts all four loaders, which is what guarantees the four
     * loading flags are set again and cleared again by the current token, so
     * abandoning an older token can never leave isBusy stuck true.
     *
     * @return undefined
     */
    load () {
      if (this.rangeError !== null) {
        return
      }

      const token = ++this.loadSequence

      this.applied = { ...this.criteria }
      this.summary = null
      this.top = {
        users: [],
        customers: [],
        endpoints: []
      }

      this.loadSummary(token)
      this.loadTop('users', token)
      this.loadTop('customers', token)
      this.loadTop('endpoints', token)
    },

    /**
     * Whether $token still identifies the newest load, and so may write to the
     * component. An abandoned load must not touch the data or the loading flags:
     * clearing a flag the current load set would stop the spinner and re-enable
     * Search while requests are still out.
     *
     * @param {Number} token
     * @return Boolean
     */
    isCurrentLoad (token) {
      return token === this.loadSequence
    },

    /**
     * Loads the totals and the daily series.
     *
     * @param {Number} token Sequence number of the load this belongs to.
     * @return Promise
     */
    async loadSummary (token) {
      this.summaryLoading = true

      try {
        const result = await this.$httpAdapter.get('/api-access-log-statistics/summary', {
          params: this.rangeParams
        })

        if (this.isCurrentLoad(token)) {
          this.summary = result.data
        }
      } catch (error) {
        if (this.isCurrentLoad(token)) {
          this.summary = null
        }

        this.handleError(error)
      } finally {
        if (this.isCurrentLoad(token)) {
          this.summaryLoading = false
        }
      }
    },

    /**
     * Loads one top list.
     *
     * @param {String} kind One of users, customers, endpoints.
     * @param {Number} token Sequence number of the load this belongs to.
     * @return Promise
     */
    async loadTop (kind, token) {
      this.topLoading[kind] = true

      try {
        const result = await this.$httpAdapter.get(`/api-access-log-statistics/top-${kind}`, {
          params: this.rangeParams
        })

        if (this.isCurrentLoad(token)) {
          this.top[kind] = get(result.data, 'items', [])
        }
      } catch (error) {
        if (this.isCurrentLoad(token)) {
          this.top[kind] = []
        }

        this.handleError(error)
      } finally {
        if (this.isCurrentLoad(token)) {
          this.topLoading[kind] = false
        }
      }
    },

    /**
     * A count, or a dash where the figure has not loaded.
     *
     * @param {Number|null|undefined} value
     * @return String
     */
    formatCount (value) {
      return value == null ? '-' : Number(value).toLocaleString()
    },

    /**
     * A duration in milliseconds, or a dash. Null is not zero: the API leaves
     * both response time figures null when nothing in the range had a measured
     * duration.
     *
     * @param {Number|null|undefined} value
     * @return String
     */
    formatMilliseconds (value) {
      return value == null ? '-' : `${Number(value).toLocaleString()} ms`
    }
  },
  created () {
    this.criteria = this.getDefaultCriteria()
  },
  mounted () {
    this.load()
  }
}
</script>
